import React, { useCallback, useState } from 'react'
import { UseStepFunc, defaultStepFormProps } from '../types.d'
import { Route } from 'react-router-dom'
import * as routing from '../../constants/routes'
import { useCompleteOnSubmit } from '../use-complete-on-submit'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useShouldExecuteStep } from '../use-should-execute-step'
import { OnboardingState } from 'app/auth/components/hooks/use-onboarding-setup'
import { OnboardingStepKey } from '@cbgms/api'
import { useShouldRunInBackground } from '../use-should-run-in-background'
import { useStepData } from '../use-step-data'
import { updateRevenueGroup } from 'app/settings/state/RevenueGroups/actions'
import { RevenueGroupDefaultCTA } from './RevenueGroupDefaultCTA'
import RevenueGroupDefaultForm from './RevenueGroupDefaultForm'
import { FormOnSubmitValues } from './Types.d'

interface StepInputProps {
  basePath: string
}

interface StepProps {
  onSubmit: (v: FormOnSubmitValues) => void
  onboardingInfo: OnboardingState
  basePath: string
}

export const useRevenueGroupDefaultStep: UseStepFunc<StepProps, StepInputProps> = ({ basePath, onboardingInfo }) => {
  const [revenueGroups, setRevenueGroup] = useState<FormOnSubmitValues | null>(null)

  const dispatch = useDispatch()
  const stepName = OnboardingStepKey.RevenueGroupDefaults
  const stepData = useStepData(stepName, onboardingInfo)
  const shouldExecuteStep = useShouldExecuteStep(stepName, onboardingInfo)
  const shouldRunInBackground = useShouldRunInBackground(stepName, onboardingInfo)

  const onStepSubmit = (values: FormOnSubmitValues) => {
    setRevenueGroup(values)
  }

  const complete = useCompleteOnSubmit(onStepSubmit)
  const onPersist = useCallback(async () => {
    if (revenueGroups) {
      const allOptions = revenueGroups?.OldValues.map(a => a.options).flat() || []
      const uuidsSetAsDefault = Object.values(revenueGroups.NewValues).filter(a => !!a)
      const funcSetNewDefault = async (uuid: string) => {
        const updateValue = { ...allOptions.find(a => a?.value === uuid)?.orig, IsDefaultForProductType: true }
        return dispatch(updateRevenueGroup(uuid, updateValue))
      }
      await Promise.all(uuidsSetAsDefault.map(funcSetNewDefault))
    }
  }, [revenueGroups, dispatch])

  return {
    Component: RevenueGroupDefaultStep,
    CTAComponent: RevenueGroupDefaultCTA,
    StepperText,
    isCompleted: complete.isCompleted,
    showInStepper: false,
    showStepperHeader: false,
    hideSkip: true,
    step: stepName,
    stepData,
    shouldExecuteStep,
    shouldRunInBackground,
    stepProps: { duplicationCheck: stepData.DuplicationCheck || [], basePath, onSubmit: complete.onSubmit, onboardingInfo },
    persister: {
      onPersist,
      persistOrder: 3
    }
  }
}

const StepperText: React.FC<{}> = () => {
  const { t } = useTranslation()
  return <>{t('onboarding.title_revenue_group_default')}</>
}

export const RevenueGroupDefaultStep: React.FC<StepProps & defaultStepFormProps> = ({ basePath, ...rest }) => {
  return <Route path={routing.pathRevenueGroupDefaults(basePath)} render={() => <RevenueGroupDefaultForm {...rest} />} />
}
